import React, { useEffect, useMemo, useState } from 'react';
import { http } from "../lib/axios";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AdminWorkspaceNav from '../components/AdminWorkspaceNav';
import ContextHelpButton from '../components/ContextHelpButton';

const ARTWORK_TYPE_OPTIONS = [
  { value: 'painting', label: 'Painting' },
  { value: 'sculpture', label: 'Sculpture' },
  { value: 'photography', label: 'Photography' },
  { value: 'pottery', label: 'Pottery' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'mixed_media', label: 'Mixed Media' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'hosted', label: 'Hosted' },
  { value: 'approved', label: 'Approved' },
  { value: 'sold', label: 'Sold' },
  { value: 'featured', label: 'Featured' },
];

const REPRESENTATION_MODE_OPTIONS = [
  { value: 'artflow', label: 'ARTflow' },
  { value: 'gallery_represented', label: 'Gallery Represented' },
];

const glassPanelSx = {
  backgroundColor: 'rgba(255,255,255,0.72)',
  backdropFilter: 'blur(14px)',
  borderRadius: 3,
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
};

const sectionWidthSx = {
  width: '100%',
  maxWidth: 1120,
  mx: 'auto',
};

function artworkTypeLabel(value) {
  return ARTWORK_TYPE_OPTIONS.find((option) => option.value === value)?.label || value || 'Other';
}

function representationModeLabel(value) {
  return REPRESENTATION_MODE_OPTIONS.find((option) => option.value === value)?.label || value || 'ARTflow';
}

function artistDisplayName(artwork) {
  return artwork?.artist?.display_name || 'Unknown Artist';
}

function hostDisplayName(artwork) {
  return artwork?.host?.display_name || artwork?.uploaded_by_host?.name || 'Not hosted';
}

function primaryArtworkImage(artwork) {
  return artwork?.primary_image_url || artwork?.image_url || artwork?.images?.[0]?.image || '';
}

function currentHostId(artwork) {
  return artwork?.host?.id || artwork?.uploaded_by_host?.id || artwork?.gallery_representation_host?.id || null;
}

function currentHostLabel(artwork) {
  return artwork?.host?.display_name || artwork?.uploaded_by_host?.name || artwork?.gallery_representation_host?.name || 'Not hosted';
}

function uploadSourceLabel(artwork) {
  if (artwork?.uploaded_by_host?.id) return 'Host uploaded';
  return 'Artist uploaded';
}

function provenanceRows(artwork) {
  return [
    { label: 'Upload Source', value: uploadSourceLabel(artwork) },
    { label: 'Current Host', value: currentHostLabel(artwork) },
    {
      label: 'Gallery Representation Host',
      value: artwork?.gallery_representation_host?.name || 'Not represented by a gallery',
    },
    { label: 'Bill of Sale Type', value: artwork?.bill_of_sale_type || 'Not set' },
    { label: 'Representation Mode', value: representationModeLabel(artwork?.representation_mode) },
    {
      label: 'Gallery Representation Active',
      value: artwork?.gallery_representation_active ? 'Yes' : 'No',
    },
  ];
}

function salePathLabel(artwork) {
  if (artwork?.representation_mode === 'gallery_represented') return 'Gallery Sale Path';
  if (artwork?.status === 'hosted' || artwork?.host?.id) return 'ARTflow Hosted Sale Path';
  return 'Direct Artist Sale Path';
}

function salePathColor(artwork) {
  if (artwork?.representation_mode === 'gallery_represented') return 'warning';
  if (artwork?.status === 'hosted' || artwork?.host?.id) return 'info';
  return 'default';
}

function isPurchasable(artwork) {
  return ['available', 'hosted', 'approved', 'featured'].includes(artwork?.status);
}

function isGalleryUploaded(artwork) {
  return Boolean(artwork?.uploaded_by_host?.id && artwork?.representation_mode === 'gallery_represented');
}

function hasPrimaryImage(artwork) {
  return Boolean(artwork?.images?.length > 0 || artwork?.primary_image_url || artwork?.image_url);
}

function formatCurrency(amount) {
  const num = Number(amount);
  if (Number.isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function artworkCompleteness(artwork) {
  const checks = [
    artwork?.title,
    artwork?.description,
    artwork?.price,
    artwork?.artwork_type,
    artwork?.status,
    artwork?.representation_mode,
    artwork?.images?.length > 0 || artwork?.primary_image_url,
  ];
  const complete = checks.filter(Boolean).length;
  return Math.round((complete / checks.length) * 100);
}

function missingArtworkFields(artwork) {
  const missing = [];
  if (!artwork?.description) missing.push('Description');
  if (!artwork?.price && artwork?.price !== 0) missing.push('Price');
  if (!artwork?.dimensions) missing.push('Dimensions');
  if (!(artwork?.images?.length > 0 || artwork?.primary_image_url)) missing.push('Primary image');
  return missing;
}

export default function ArtworkManager() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [representationFilter, setRepresentationFilter] = useState('all');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const artistFilter = searchParams.get('artist') || 'all';
  const hostedHostFilter = searchParams.get('hosted_host') || 'all';
  const galleryHostFilter = searchParams.get('gallery_host') || 'all';

  const loadArtworks = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const response = await http.get('/artworks/?view=card');
      setArtworks(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Unable to load artworks.');
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadArtworks();
  }, []);

  useEffect(() => {
    setSelectedArtworkIds((prev) =>
      prev.filter((id) => artworks.some((artwork) => String(artwork.id) === String(id)))
    );
  }, [artworks]);

  const filteredArtworks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return artworks.filter((artwork) => {
      const haystack = [
        artwork?.title,
        artwork?.description,
        artistDisplayName(artwork),
        hostDisplayName(artwork),
        artworkTypeLabel(artwork?.artwork_type),
        artwork?.status,
        representationModeLabel(artwork?.representation_mode),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = !q || haystack.includes(q);
      const statusMatch = statusFilter === 'all' || artwork?.status === statusFilter;
      const typeMatch = typeFilter === 'all' || artwork?.artwork_type === typeFilter;
      const representationMatch =
        representationFilter === 'all' || artwork?.representation_mode === representationFilter;
      const artistMatch = artistFilter === 'all' || String(artwork?.artist?.id) === String(artistFilter);
      const hostedHostMatch =
        hostedHostFilter === 'all' || String(artwork?.host?.id) === String(hostedHostFilter);
      const galleryHostMatch =
        galleryHostFilter === 'all' ||
        String(artwork?.gallery_representation_host?.id || artwork?.uploaded_by_host?.id) === String(galleryHostFilter);

      return (
        searchMatch &&
        statusMatch &&
        typeMatch &&
        representationMatch &&
        artistMatch &&
        hostedHostMatch &&
        galleryHostMatch
      );
    });
  }, [artworks, search, statusFilter, typeFilter, representationFilter, artistFilter, hostedHostFilter, galleryHostFilter]);

  const summary = useMemo(() => {
    const galleryRepresented = artworks.filter((artwork) => artwork?.representation_mode === 'gallery_represented').length;
    const directArtflow = artworks.filter(
      (artwork) => artwork?.representation_mode !== 'gallery_represented' && artwork?.status !== 'hosted'
    ).length;
    const galleryUploaded = artworks.filter((artwork) => isGalleryUploaded(artwork)).length;
    const hosted = artworks.filter((artwork) => artwork?.status === 'hosted').length;
    const sold = artworks.filter((artwork) => artwork?.status === 'sold').length;
    const purchasable = artworks.filter((artwork) => isPurchasable(artwork)).length;
    const missingImages = artworks.filter((artwork) => !hasPrimaryImage(artwork)).length;
    const avgCompleteness = artworks.length
      ? Math.round(artworks.reduce((sum, artwork) => sum + artworkCompleteness(artwork), 0) / artworks.length)
      : 0;

    return {
      total: artworks.length,
      galleryRepresented,
      directArtflow,
      galleryUploaded,
      hosted,
      sold,
      purchasable,
      missingImages,
      avgCompleteness,
    };
  }, [artworks]);

  const allFilteredSelected =
    filteredArtworks.length > 0 &&
    filteredArtworks.every((artwork) => selectedArtworkIds.includes(String(artwork.id)));

  const selectedCount = selectedArtworkIds.length;

  const toggleArtworkSelection = (artworkId) => {
    const id = String(artworkId);
    setSelectedArtworkIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectFiltered = () => {
    const visibleIds = filteredArtworks.map((artwork) => String(artwork.id));
    if (allFilteredSelected) {
      setSelectedArtworkIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedArtworkIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const clearSelection = () => setSelectedArtworkIds([]);

  const openEditDialog = (artwork) => {
    setEditingArtwork(artwork);
    setEditForm({
      title: artwork?.title || '',
      description: artwork?.description || '',
      price: artwork?.price || '',
      artwork_type: artwork?.artwork_type || 'other',
      dimensions: artwork?.dimensions || '',
      status: artwork?.status || 'available',
      representation_mode: artwork?.representation_mode || 'artflow',
      representation_mode_change_reason: '',
    });
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditingArtwork(null);
    setEditForm(null);
  };

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingArtwork?.id || !editForm) return;
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (
        editingArtwork?.representation_mode !== 'artflow' ||
        payload.representation_mode !== 'gallery_represented'
      ) {
        delete payload.representation_mode_change_reason;
      }
      await http.patch(`/artworks/${editingArtwork.id}/`, payload);
      await loadArtworks({ silent: true });
      setSnackbar({ open: true, message: 'Artwork updated.', severity: 'success' });
      closeEditDialog();
    } catch (err) {
      const data = err?.response?.data;
      const detail =
        typeof data === 'string'
          ? data
          : data?.detail ||
            Object.values(data || {})
              .flat()
              .join(' ') ||
            'Failed to update the artwork.';
      setSnackbar({
        open: true,
        message: detail,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await http.delete(`/artworks/${deleteTarget.id}/`);
      setArtworks((prev) => prev.filter((artwork) => artwork.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'Artwork deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to delete the artwork.',
        severity: 'error',
      });
    }
  };

  const handleMarkSold = async (artwork) => {
    try {
      await http.patch(`/artworks/${artwork.id}/mark_sold/`);
      await loadArtworks({ silent: true });
      setSnackbar({ open: true, message: 'Artwork marked as sold.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to mark the artwork as sold.',
        severity: 'error',
      });
    }
  };

  const handleBulkStatusUpdate = async ({ status, label }) => {
    if (!selectedArtworkIds.length) return;
    let successCount = 0;
    for (const artworkId of selectedArtworkIds) {
      try {
        await http.patch(`/artworks/${artworkId}/`, { status });
        successCount += 1;
      } catch (err) {
        // Keep going so admins can resolve partial success from the resulting refreshed state.
      }
    }
    await loadArtworks({ silent: true });
    clearSelection();
    setSnackbar({
      open: true,
      message:
        successCount === selectedArtworkIds.length
          ? `${label} applied to ${successCount} artwork${successCount === 1 ? '' : 's'}.`
          : `${label} applied to ${successCount} of ${selectedArtworkIds.length} selected artworks.`,
      severity: successCount > 0 ? 'success' : 'error',
    });
  };

  const handleBulkMarkSold = async () => {
    if (!selectedArtworkIds.length) return;
    let successCount = 0;
    for (const artworkId of selectedArtworkIds) {
      try {
        await http.patch(`/artworks/${artworkId}/mark_sold/`);
        successCount += 1;
      } catch (err) {
        // Continue so the admin still gets the batch effect where possible.
      }
    }
    await loadArtworks({ silent: true });
    clearSelection();
    setSnackbar({
      open: true,
      message:
        successCount === selectedArtworkIds.length
          ? `Marked ${successCount} artwork${successCount === 1 ? '' : 's'} as sold.`
          : `Marked ${successCount} of ${selectedArtworkIds.length} selected artworks as sold.`,
      severity: successCount > 0 ? 'success' : 'error',
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedArtworkIds.length) return;
    const confirmed = window.confirm(
      `Delete ${selectedArtworkIds.length} selected artwork${selectedArtworkIds.length === 1 ? '' : 's'}? This cannot be undone.`
    );
    if (!confirmed) return;
    let successCount = 0;
    for (const artworkId of selectedArtworkIds) {
      try {
        await http.delete(`/artworks/${artworkId}/`);
        successCount += 1;
      } catch (err) {
        // Continue so the refreshed list shows what could not be deleted.
      }
    }
    await loadArtworks({ silent: true });
    clearSelection();
    setSnackbar({
      open: true,
      message:
        successCount === selectedArtworkIds.length
          ? `Deleted ${successCount} artwork${successCount === 1 ? '' : 's'}.`
          : `Deleted ${successCount} of ${selectedArtworkIds.length} selected artworks.`,
      severity: successCount > 0 ? 'success' : 'error',
    });
  };

  return (
    <Container sx={{ mt: 5, mb: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              Artwork Manager
            </Typography>
            <ContextHelpButton section="admin-help" title="Help for artwork manager" />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage existing artwork records inside the admin workspace, review sales-readiness, and update the live artwork fields that drive hosting and purchase behavior.
          </Typography>
        </Box>

        <Box sx={sectionWidthSx}>
          <AdminWorkspaceNav subtitle="Use the shared admin navigation to move between artworks, artists, hosts, and buyers under the same /admin namespace." />
        </Box>

        <Grid container spacing={2} sx={sectionWidthSx}>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Artworks</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Direct ARTflow</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.directArtflow}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">ARTflow Hosted</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.hosted}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Gallery Represented</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.galleryRepresented}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Gallery Uploaded</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.galleryUploaded}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Average Completeness</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.avgCompleteness}%</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Purchasable</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.purchasable}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Sold</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.sold}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Missing Primary Image</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.missingImages}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search artworks"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Title, description, artist, host, status..."
              fullWidth
            />
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Artwork type"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 180 } }}
            >
              <MenuItem value="all">All types</MenuItem>
              {ARTWORK_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Representation"
              value={representationFilter}
              onChange={(event) => setRepresentationFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 220 } }}
            >
              <MenuItem value="all">All modes</MenuItem>
              {REPRESENTATION_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setTypeFilter('all');
                setRepresentationFilter('all');
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('artist');
                nextParams.delete('hosted_host');
                nextParams.delete('gallery_host');
                setSearchParams(nextParams, { replace: true });
              }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" onClick={() => loadArtworks({ silent: true })} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredArtworks.length} of {artworks.length} artworks.
            {artistFilter !== 'all' ? ` Filtered to artist #${artistFilter}.` : ''}
            {hostedHostFilter !== 'all' ? ` Filtered to hosted artworks for host #${hostedHostFilter}.` : ''}
            {galleryHostFilter !== 'all' ? ` Filtered to gallery inventory for host #${galleryHostFilter}.` : ''}
          </Typography>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button variant="outlined" onClick={toggleSelectFiltered} disabled={!filteredArtworks.length}>
                {allFilteredSelected ? 'Clear Visible Selection' : 'Select Visible'}
              </Button>
              <Button variant="outlined" onClick={clearSelection} disabled={!selectedCount}>
                Clear Selection
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedCount} selected
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                onClick={() => handleBulkStatusUpdate({ status: 'featured', label: 'Featured status' })}
                disabled={!selectedCount}
              >
                Mark Featured
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleBulkMarkSold} disabled={!selectedCount}>
                Mark Sold
              </Button>
              <Button variant="outlined" color="error" onClick={handleBulkDelete} disabled={!selectedCount}>
                Delete Selected
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography>Loading artworks…</Typography>
          </Paper>
        ) : filteredArtworks.length === 0 ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography color="text.secondary">No artworks match the current filters.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={sectionWidthSx}>
            {filteredArtworks.map((artwork) => (
              <Grid item xs={12} key={artwork.id}>
                <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', lg: 'stretch' }}
                    sx={{ minHeight: { lg: 220 } }}
                  >
                    <Stack
                      spacing={1.5}
                      sx={{
                        width: { xs: '100%', lg: 184 },
                        flexShrink: 0,
                      }}
                    >
                      {primaryArtworkImage(artwork) ? (
                        <Box
                          component="img"
                          src={primaryArtworkImage(artwork)}
                          alt={artwork?.title || 'Artwork thumbnail'}
                          sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                            borderRadius: 2,
                            border: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: 'rgba(255,255,255,0.65)',
                          }}
                        />
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{
                            height: 150,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2,
                            textAlign: 'center',
                            color: 'text.secondary',
                            backgroundColor: 'rgba(255,255,255,0.55)',
                          }}
                        >
                          <Typography variant="body2">No primary image</Typography>
                        </Paper>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {artwork?.images?.length || 0} image{artwork?.images?.length === 1 ? '' : 's'}
                      </Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        sx={{ mb: 1 }}
                      >
                        <Checkbox
                          checked={selectedArtworkIds.includes(String(artwork.id))}
                          onChange={() => toggleArtworkSelection(artwork.id)}
                          inputProps={{ 'aria-label': `Select ${artwork?.title || 'artwork'}` }}
                          sx={{ p: 0.5, ml: -0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Select for bulk actions
                        </Typography>
                      </Stack>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {artwork?.title || 'Untitled Artwork'}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Chip size="small" label={`Completeness ${artworkCompleteness(artwork)}%`} />
                        <Chip size="small" variant="outlined" label={artworkTypeLabel(artwork?.artwork_type)} />
                        <Chip size="small" color={artwork?.status === 'sold' ? 'success' : 'default'} label={artwork?.status || 'unknown'} />
                        <Chip size="small" variant="outlined" label={representationModeLabel(artwork?.representation_mode)} />
                        <Chip size="small" color={salePathColor(artwork)} label={salePathLabel(artwork)} />
                        <Chip size="small" color={isPurchasable(artwork) ? 'success' : 'default'} label={isPurchasable(artwork) ? 'Purchasable' : 'Not Purchasable'} />
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Artist: {artistDisplayName(artwork)} | Host: {hostDisplayName(artwork)}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {artwork?.description || 'No artwork description yet.'}
                      </Typography>

                      <Grid container spacing={1.5} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Price</Typography>
                          <Typography variant="body2">{formatCurrency(artwork?.price)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Dimensions</Typography>
                          <Typography variant="body2">{artwork?.dimensions || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Bill of Sale</Typography>
                          <Typography variant="body2">{artwork?.bill_of_sale_type || 'Not set'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Image Count</Typography>
                          <Typography variant="body2">{artwork?.images?.length || 0}</Typography>
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" sx={{ mt: 1.75 }}>
                        <Chip size="small" variant="outlined" label={`Upload: ${uploadSourceLabel(artwork)}`} />
                        <Chip size="small" variant="outlined" label={`Current Host: ${currentHostLabel(artwork)}`} />
                        <Chip size="small" variant="outlined" label={hasPrimaryImage(artwork) ? 'Image Ready' : 'Missing Primary Image'} />
                        {artwork?.gallery_representation_host?.name && (
                          <Chip size="small" variant="outlined" label={`Gallery Rep: ${artwork.gallery_representation_host.name}`} />
                        )}
                      </Stack>

                      <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        <Button variant="outlined" onClick={() => setSelectedArtwork(artwork)}>
                          View Details
                        </Button>
                        <Button variant="outlined" onClick={() => navigate(`/artworks/${artwork.id}`)}>
                          Open Artwork
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/admin/artists?artist=${artwork?.artist?.id || ''}`)}
                          disabled={!artwork?.artist?.id}
                        >
                          Artist Admin
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/artists/${artwork.artist.id}`)}
                          disabled={!artwork?.artist?.id}
                        >
                          Artist Profile
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/admin/hosts?host=${currentHostId(artwork) || ''}`)}
                          disabled={!currentHostId(artwork)}
                        >
                          Host Admin
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/hosts/${currentHostId(artwork)}`)}
                          disabled={!currentHostId(artwork)}
                        >
                          Host Profile
                        </Button>
                        <Button variant="outlined" onClick={() => openEditDialog(artwork)}>
                          Edit
                        </Button>
                        {artwork?.status !== 'sold' && (
                          <Button variant="outlined" color="secondary" onClick={() => handleMarkSold(artwork)}>
                            Mark Sold
                          </Button>
                        )}
                        <Button variant="outlined" color="error" onClick={() => setDeleteTarget(artwork)}>
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog open={Boolean(selectedArtwork)} onClose={() => setSelectedArtwork(null)} fullWidth maxWidth="md">
        <DialogTitle>Artwork Details</DialogTitle>
        <DialogContent dividers>
          {selectedArtwork && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedArtwork?.title || 'Untitled Artwork'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Artist: {artistDisplayName(selectedArtwork)} | Host: {hostDisplayName(selectedArtwork)}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Completeness ${artworkCompleteness(selectedArtwork)}%`} />
                <Chip variant="outlined" label={artworkTypeLabel(selectedArtwork?.artwork_type)} />
                <Chip label={selectedArtwork?.status || 'unknown'} />
                <Chip variant="outlined" label={representationModeLabel(selectedArtwork?.representation_mode)} />
                <Chip color={salePathColor(selectedArtwork)} label={salePathLabel(selectedArtwork)} />
                <Chip color={isPurchasable(selectedArtwork) ? 'success' : 'default'} label={isPurchasable(selectedArtwork) ? 'Purchasable' : 'Not Purchasable'} />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  {primaryArtworkImage(selectedArtwork) ? (
                    <Box
                      component="img"
                      src={primaryArtworkImage(selectedArtwork)}
                      alt={selectedArtwork?.title || 'Artwork preview'}
                      sx={{
                        width: '100%',
                        height: { xs: 220, md: 320 },
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.65)',
                      }}
                    />
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        height: { xs: 220, md: 320 },
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 2,
                        textAlign: 'center',
                        color: 'text.secondary',
                        backgroundColor: 'rgba(255,255,255,0.55)',
                      }}
                    >
                      <Typography variant="body2">No primary image available for this artwork yet.</Typography>
                    </Paper>
                  )}
                </Grid>
                <Grid item xs={12} md={5}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Provenance & Hosting
                    </Typography>
                    <Stack spacing={1.25}>
                      {provenanceRows(selectedArtwork).map((row) => (
                        <Box key={row.label}>
                          <Typography variant="caption" color="text.secondary">
                            {row.label}
                          </Typography>
                          <Typography variant="body2">{row.value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedArtwork?.description || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Price</Typography>
                  <Typography variant="body2">{formatCurrency(selectedArtwork?.price)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Dimensions</Typography>
                  <Typography variant="body2">{selectedArtwork?.dimensions || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Bill of Sale Type</Typography>
                  <Typography variant="body2">{selectedArtwork?.bill_of_sale_type || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Image Count</Typography>
                  <Typography variant="body2">{selectedArtwork?.images?.length || 0}</Typography>
                </Grid>
              </Grid>

              <Alert severity={missingArtworkFields(selectedArtwork).length > 0 ? 'warning' : 'success'}>
                {missingArtworkFields(selectedArtwork).length > 0
                  ? `Missing artwork details: ${missingArtworkFields(selectedArtwork).join(', ')}`
                  : 'This artwork has the core fields filled in.'}
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigate(`/admin/artists?artist=${selectedArtwork?.artist?.id || ''}`)}
            disabled={!selectedArtwork?.artist?.id}
          >
            Artist Admin
          </Button>
          <Button
            onClick={() => navigate(`/artists/${selectedArtwork?.artist?.id || ''}`)}
            disabled={!selectedArtwork?.artist?.id}
          >
            Artist Profile
          </Button>
          <Button
            onClick={() => navigate(`/admin/hosts?host=${currentHostId(selectedArtwork) || ''}`)}
            disabled={!currentHostId(selectedArtwork)}
          >
            Host Admin
          </Button>
          <Button
            onClick={() => navigate(`/hosts/${currentHostId(selectedArtwork) || ''}`)}
            disabled={!currentHostId(selectedArtwork)}
          >
            Host Profile
          </Button>
          <Button onClick={() => setSelectedArtwork(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = selectedArtwork;
              setSelectedArtwork(null);
              if (target) openEditDialog(target);
            }}
          >
            Edit Artwork
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingArtwork)} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Edit Artwork</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Title"
                value={editForm.title}
                onChange={(event) => handleEditField('title', event.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={editForm.description}
                onChange={(event) => handleEditField('description', event.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Price"
                  type="number"
                  value={editForm.price}
                  onChange={(event) => handleEditField('price', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Dimensions"
                  value={editForm.dimensions}
                  onChange={(event) => handleEditField('dimensions', event.target.value)}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Artwork type"
                  value={editForm.artwork_type}
                  onChange={(event) => handleEditField('artwork_type', event.target.value)}
                  fullWidth
                >
                  {ARTWORK_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Status"
                  value={editForm.status}
                  onChange={(event) => handleEditField('status', event.target.value)}
                  fullWidth
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                select
                label="Representation mode"
                value={editForm.representation_mode}
                onChange={(event) => handleEditField('representation_mode', event.target.value)}
                fullWidth
              >
                {REPRESENTATION_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              {editingArtwork?.representation_mode === 'artflow' && editForm.representation_mode === 'gallery_represented' && (
                <TextField
                  label="Reason for switching to gallery represented"
                  value={editForm.representation_mode_change_reason}
                  onChange={(event) => handleEditField('representation_mode_change_reason', event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Artwork</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete the artwork <strong>{deleteTarget?.title || ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This is an admin action and may remove live listing data, hosting state, and sales context, so it should be used carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
